import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { CreateLoaderTable } from "@/components/Loaders";
import { Mask } from "@/utils/mask";
import { CheckCircle, XCircle, Eye, Loader, TrendingUp, TrendingDown, Package, Percent, Trophy, Clock, FileSpreadsheet, Loader2 } from "lucide-react";
import { ContestProfit } from "@/types/contestProfit";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  getContestProfitDetails,
  exportContestProfitDetails,
  ContestProfitDetails,
  ContestProfitProduct,
} from "@/Api/contests";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";

interface TableDataProps {
  dataApi: {
    data: ContestProfit[];
    current_page: number;
    last_page: number;
    total: number;
  };
  pageActions: {
    next: () => void;
    previous: () => void;
    goToPage: (page: number) => void;
  };
  state: string;
}

export const TableData = ({
  dataApi,
  pageActions,
  state,
}: TableDataProps) => {
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [contestDetails, setContestDetails] = useState<ContestProfitDetails | null>(null);
  const [exporting, setExporting] = useState(false);

  const getProfitColor = (profit: number): string => {
    return profit >= 0
      ? "text-green-600 font-semibold"
      : "text-red-600 font-semibold";
  };

  const getProfitIcon = (profit: number) => {
    return profit >= 0 ? (
      <CheckCircle className="inline-block ml-2 h-5 w-5 text-green-600" />
    ) : (
      <XCircle className="inline-block ml-2 h-5 w-5 text-red-600" />
    );
  };

  const formatSigned = (value: number) =>
    `${value >= 0 ? "+" : ""}${Mask.formatCurrency(value)}`;

  const signedColor = (value: number) =>
    value >= 0 ? "text-green-600" : "text-red-600";

  // Valores dos cards numa linha só: sem quebrar o sinal do número
  const summaryValueClass = "text-xl font-bold whitespace-nowrap tabular-nums";

  const resultCardClass = (value: number) =>
    value >= 0
      ? "border-green-200 bg-green-50 dark:bg-green-900/20"
      : "border-red-200 bg-red-50 dark:bg-red-900/20";

  const finalProfit = (details: ContestProfitDetails) =>
    details.summary.total_final_profit ?? details.summary.total_profit;

  const isInvestigated = (product: ContestProfitProduct) =>
    product.investigation_status === "awarded" ||
    product.investigation_status === "not_awarded";

  const renderUnsoldPrize = (product: ContestProfitProduct) => {
    if (!isInvestigated(product)) {
      return (
        <span
          className="inline-flex items-center gap-1 text-xs text-gray-400"
          title={
            product.investigation_status === "not_generated"
              ? "Apuração ainda não gerada para este bolão"
              : "Bolão ainda não apurado"
          }
        >
          <Clock className="h-3 w-3" />
          Pendente
        </span>
      );
    }

    if (!product.unsold_prize_value) {
      return (
        <span
          className="text-gray-400"
          title={
            product.investigation_status === "not_awarded"
              ? "Bolão não premiado"
              : "Sem cotas não vendidas"
          }
        >
          -
        </span>
      );
    }

    return (
      <span
        className="font-semibold text-green-600"
        title={`${product.quotas_not_sold} cota(s) não vendida(s) x ${Mask.formatCurrency(product.prize_per_quota || 0)}`}
      >
        +{Mask.formatCurrency(product.unsold_prize_value)}
      </span>
    );
  };

  const handleOpenDetails = async (contestId: number) => {
    setDetailsModalOpen(true);
    setDetailsLoading(true);
    try {
      const response = await getContestProfitDetails(contestId);
      if (response.ok) {
        setContestDetails(response.data?.response);
      } else {
        toast.error("Erro ao carregar detalhes do lucro");
      }
    } catch (error) {
      console.error("Erro ao buscar detalhes:", error);
      toast.error("Erro ao carregar detalhes do lucro");
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleExportExcel = async () => {
    if (!contestDetails) return;

    setExporting(true);
    try {
      const blob = await exportContestProfitDetails(contestDetails.contest.id);
      if (!blob) return;

      const safeName = `${contestDetails.lottery.name}-${contestDetails.contest.name}`
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .toLowerCase();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `lucro-concurso-${safeName}-${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Planilha baixada!");
    } catch (error) {
      console.error("Erro ao exportar lucro do concurso:", error);
      toast.error("Erro ao baixar a planilha");
    } finally {
      setExporting(false);
    }
  };

  const handleCloseDetails = () => {
    setDetailsModalOpen(false);
    setContestDetails(null);
  };

  if (state === "loading") {
    return <CreateLoaderTable cols={7} lines={8} />;
  }

  return (
    <>
      <div className="rounded-md border">
        <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
          <TableHeader>
            <TableRow className="bg-blue-600 hover:bg-blue-600">
              <TableHead className="text-white font-bold">Concurso</TableHead>
              <TableHead className="text-white font-bold">Loteria</TableHead>
              <TableHead className="text-white font-bold">Data de Validação</TableHead>
              <TableHead className="text-white font-bold text-right">
                Total Vendido
              </TableHead>
              <TableHead className="text-white font-bold text-right">
                Custo
              </TableHead>
              <TableHead className="text-white font-bold text-right">
                Resultado
              </TableHead>
              <TableHead className="text-white font-bold text-center">
                Ações
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {dataApi?.data?.length > 0 ? (
              dataApi.data.map((item) => (
                <TableRow key={item.contest.id} className="hover:bg-zinc-100 dark:hover:bg-zinc-800">
                  <TableCell className="font-medium">
                    {item.contest.name}
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: item.lottery.color }}
                      />
                      {item.lottery.name}
                    </div>
                  </TableCell>
                  <TableCell>
                    {item.contest.validation_date
                      ? format(new Date(item.contest.validation_date + "T00:00:00"), "dd/MM/yyyy")
                      : "-"}
                  </TableCell>
                  <TableCell className="text-right">
                    {Mask.formatCurrency(item.summary.total_revenue)}
                  </TableCell>
                  <TableCell className="text-right">
                    {Mask.formatCurrency(item.summary.total_cost)}
                  </TableCell>
                  <TableCell className={`text-right ${getProfitColor(item.summary.total_profit)}`}>
                    {item.summary.total_profit >= 0 ? "+" : ""}
                    {Mask.formatCurrency(item.summary.total_profit)}
                    {getProfitIcon(item.summary.total_profit)}
                  </TableCell>
                  <TableCell className="text-center">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenDetails(item.contest.id)}
                      className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
                    >
                      <Eye className="size-5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8">
                  Nenhum registro encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {dataApi && dataApi.last_page > 1 && (
        <Pagination className="mt-4">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                onClick={() => {
                  if (dataApi.current_page > 1) {
                    pageActions.previous();
                  }
                }}
                className={
                  dataApi.current_page === 1
                    ? "pointer-events-none opacity-50"
                    : "cursor-pointer"
                }
              />
            </PaginationItem>

            {Array.from({ length: dataApi.last_page }, (_, i) => i + 1)
              .filter((page) => {
                const current = dataApi.current_page;
                return (
                  page === 1 ||
                  page === dataApi.last_page ||
                  (page >= current - 1 && page <= current + 1)
                );
              })
              .map((page, index, array) => {
                if (index > 0 && array[index - 1] !== page - 1) {
                  return (
                    <PaginationItem key={`ellipsis-${page}`}>
                      <span className="px-2">...</span>
                    </PaginationItem>
                  );
                }
                return (
                  <PaginationItem key={page}>
                    <PaginationLink
                      onClick={() => pageActions.goToPage(page)}
                      isActive={page === dataApi.current_page}
                      className={`cursor-pointer ${
                        page !== dataApi.current_page
                          ? "hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100"
                          : ""
                      }`}
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

            <PaginationItem>
              <PaginationNext
                onClick={() => {
                  if (dataApi.current_page < dataApi.last_page) {
                    pageActions.next();
                  }
                }}
                className={
                  dataApi.current_page === dataApi.last_page
                    ? "pointer-events-none opacity-50"
                    : "cursor-pointer"
                }
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}

      <Dialog open={detailsModalOpen} onOpenChange={(open) => !open && handleCloseDetails()}>
        <DialogContent className="w-[96vw] sm:max-w-[1440px] max-h-[92vh]">
          {contestDetails && (
            <DialogHeader>
              <div
                className="p-4 rounded-t-lg -mt-6 -mx-6 mb-4"
                style={{ backgroundColor: contestDetails.lottery.color }}
              >
                <DialogTitle className="text-white text-xl">
                  {contestDetails.lottery.name} - {contestDetails.contest.name}
                </DialogTitle>
                <DialogDescription className="text-white/80">
                  {contestDetails.contest.validation_date &&
                    `Data de Validação: ${format(new Date(contestDetails.contest.validation_date + "T00:00:00"), "dd/MM/yyyy")}`}
                </DialogDescription>
              </div>
            </DialogHeader>
          )}

          {detailsLoading ? (
            <div className="flex justify-center items-center h-40">
              <Loader className="animate-spin h-8 w-8 text-blue-600" />
            </div>
          ) : contestDetails ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3 mb-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-blue-600 mb-2 whitespace-nowrap">
                      <Package className="h-4 w-4 shrink-0" />
                      <span className="text-sm font-medium">Total Vendido</span>
                    </div>
                    <p className={summaryValueClass}>
                      {Mask.formatCurrency(contestDetails.summary.total_sold_value)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {contestDetails.summary.total_quotas_sold} cotas vendidas
                    </p>
                    {contestDetails.summary.total_quotas_sold_with_discount > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <p className="text-xs text-purple-600 flex items-center gap-1">
                          <Percent className="h-3 w-3" />
                          {contestDetails.summary.total_quotas_sold_with_discount} cotas com desconto
                        </p>
                        <p className="text-xs text-purple-700 font-medium mt-1">
                          -{Mask.formatCurrency(contestDetails.summary.total_discount_value)} em descontos
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-orange-600 mb-2 whitespace-nowrap">
                      <TrendingDown className="h-4 w-4 shrink-0" />
                      <span className="text-sm font-medium">Custo Total</span>
                    </div>
                    <p className={summaryValueClass}>
                      {Mask.formatCurrency(contestDetails.summary.total_cost)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {contestDetails.summary.total_quotas_not_sold} cotas não vendidas
                    </p>
                  </CardContent>
                </Card>
                <Card className={resultCardClass(contestDetails.summary.total_profit)}>
                  <CardContent className="p-4">
                    <div className={`flex items-center gap-2 mb-2 whitespace-nowrap ${signedColor(contestDetails.summary.total_profit)}`}>
                      <TrendingUp className="h-4 w-4 shrink-0" />
                      <span className="text-sm font-medium">Resultado sem prêmios</span>
                    </div>
                    <p className={`${summaryValueClass} ${signedColor(contestDetails.summary.total_profit)}`}>
                      {formatSigned(contestDetails.summary.total_profit)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {contestDetails.summary.total_profit >= 0 ? "Lucro" : "Prejuízo"} (vendido - custo)
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-amber-600 mb-2 whitespace-nowrap">
                      <Trophy className="h-4 w-4 shrink-0" />
                      <span className="text-sm font-medium">Premiações</span>
                    </div>
                    {contestDetails.summary.investigation?.status === "pending" ? (
                      <>
                        <p className="flex items-center gap-1.5 text-lg font-semibold text-gray-400 whitespace-nowrap">
                          <Clock className="h-4 w-4 shrink-0" />
                          Apuração pendente
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Prêmios das cotas não vendidas
                        </p>
                      </>
                    ) : (
                      <>
                        <p className={`${summaryValueClass} text-green-600`}>
                          +{Mask.formatCurrency(contestDetails.summary.total_unsold_prize || 0)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {contestDetails.summary.total_unsold_prize_quotas || 0} cotas não vendidas premiadas
                        </p>
                        {contestDetails.summary.investigation?.status === "partial" && (
                          <p className="text-xs text-amber-600 mt-1">
                            {contestDetails.summary.investigation.investigated_products} de{" "}
                            {contestDetails.summary.investigation.total_products} bolões apurados
                          </p>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>
                <Card className={resultCardClass(finalProfit(contestDetails))}>
                  <CardContent className="p-4">
                    <div className={`flex items-center gap-2 mb-2 whitespace-nowrap ${signedColor(finalProfit(contestDetails))}`}>
                      <TrendingUp className="h-4 w-4 shrink-0" />
                      <span className="text-sm font-medium">Resultado final</span>
                    </div>
                    <p className={`${summaryValueClass} ${signedColor(finalProfit(contestDetails))}`}>
                      {formatSigned(finalProfit(contestDetails))}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {contestDetails.summary.investigation?.status === "completed"
                        ? `${finalProfit(contestDetails) >= 0 ? "Lucro" : "Prejuízo"} com prêmios`
                        : "Parcial: aguardando apuração"}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <ScrollArea className="h-[45vh]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produto</TableHead>
                      <TableHead className="text-right">Cotas Vendidas</TableHead>
                      <TableHead className="text-right">Cotas Não Vendidas</TableHead>
                      <TableHead className="text-right">Valor Vendido</TableHead>
                      <TableHead className="text-right">Custo</TableHead>
                      <TableHead className="text-right">Resultado sem prêmios</TableHead>
                      <TableHead className="text-right">Prêmios cotas não vendidas</TableHead>
                      <TableHead className="text-right">Resultado final</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contestDetails.products && contestDetails.products.length > 0 ? (
                      contestDetails.products.map((product) => (
                        <TableRow key={product.cp_classification_id}>
                          <TableCell className="whitespace-nowrap">
                            <span className="font-semibold">{product.product_name}</span>
                            <span className="text-gray-500 dark:text-gray-400">
                              {" - "}
                              {product.product_classification?.name}
                            </span>
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap tabular-nums">
                            {product.quotas_sold}
                            {product.quotas_sold_with_discount > 0 && (
                              <span
                                className="text-xs text-purple-600 ml-1"
                                title={`${product.quotas_sold_with_discount} cotas com desconto = -${Mask.formatCurrency(product.discount_value)}`}
                              >
                                ({product.quotas_sold_with_discount} c/ desc.)
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-gray-500 tabular-nums">
                            {product.quotas_not_sold}
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap tabular-nums">
                            {Mask.formatCurrency(product.sold_value)}
                          </TableCell>
                          <TableCell className="text-right text-orange-600 whitespace-nowrap tabular-nums">
                            {Mask.formatCurrency(product.cost)}
                          </TableCell>
                          <TableCell className={`text-right font-semibold whitespace-nowrap tabular-nums ${signedColor(product.profit)}`}>
                            {formatSigned(product.profit)}
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap tabular-nums">
                            {renderUnsoldPrize(product)}
                          </TableCell>
                          <TableCell className={`text-right font-semibold whitespace-nowrap tabular-nums ${signedColor(product.final_profit ?? product.profit)}`}>
                            {formatSigned(product.final_profit ?? product.profit)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-4 text-gray-500">
                          Nenhum produto encontrado.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </ScrollArea>
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              Erro ao carregar detalhes.
            </div>
          )}

          <DialogFooter className="mt-4 gap-2">
            <Button
              onClick={handleExportExcel}
              disabled={!contestDetails || detailsLoading || exporting}
              className="bg-green-600 text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600"
            >
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <FileSpreadsheet className="h-4 w-4 mr-2" />
              )}
              {exporting ? "Gerando..." : "Baixar Excel"}
            </Button>
            <Button variant="outline" onClick={handleCloseDetails}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
